import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import type { Socket } from 'socket.io-client'
import { io } from 'socket.io-client'
import config from 'constants/config'
import {
  set as setLocalStorage,
  get as getLocalStorage,
  remove as removeLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { authLogout } from 'actions/AuthActions'
import type { ClientToServerEvents, ServerToClientEvents } from 'api/supportAssistant'
import { CONNECTION, SESSION } from 'api/supportAssistant'
import { selectLoggedIn } from 'selectors/accountSelectors'

export interface SessionSocket<T> extends Socket {
  sessionId?: string
  customerId?: string
  events?: T
}

export type SocketType = SessionSocket<ClientToServerEvents | ServerToClientEvents>

export let globalSocket: SocketType | undefined = undefined

export const socket: SocketType | undefined = undefined

let lastExpiredAt = 0
const EXPIRY_COOLDOWN_MS = 5000
const SOCKET_RESET_TIMEOUT_MS = 15000

function disconnectGlobalSocket() {
  if (globalSocket) {
    globalSocket.disconnect()
    globalSocket = undefined
  }
}

export default function useSocket() {
  const dispatch = useAppDispatch()
  const [socket, setSocket] = useState<SocketType | undefined>(undefined)
  const socketRef = useRef(socket)
  socketRef.current = socket

  const userIsLoggedIn = useAppSelector(selectLoggedIn)
  const customerId = useAppSelector((state) => state.auth.account?.memberId)
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const locale = useAppSelector((state) => state.geo.locale)

  useEffect(() => {
    if (!globalSocket) {
      const sessionId = getLocalStorage('supportAssistantSessionId')

      globalSocket = io(config.API_HOST, {
        transports: ['websocket', 'polling'],
        path: '/api/support/chat/socket.io',
        auth: {
          sessionId,
          customerId,
          accessToken,
          locale,
        },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
      })
    }
    if (!socket) {
      setSocket(globalSocket)
    }
  }, [accessToken, customerId, dispatch, socket, locale])

  const resetSocket = useCallback(({ clearStoredSession = false }: { clearStoredSession?: boolean } = {}) => {
    return new Promise<void>((resolve) => {
      if (clearStoredSession) {
        removeLocalStorage('supportAssistantSessionId')
        removeLocalStorage('supportAssistantCustomerId')
      }

      disconnectGlobalSocket()
      setSocket(undefined)
      const resetStartedAt = Date.now()

      const checkSocket = () => {
        if (globalSocket?.connected && socketRef.current === globalSocket) {
          resolve()
        } else if (Date.now() - resetStartedAt >= SOCKET_RESET_TIMEOUT_MS) {
          resolve()
        } else {
          setTimeout(checkSocket, 50)
        }
      }

      checkSocket()
    })
  }, [])

  useEffect(() => {
    if (!userIsLoggedIn) {
      removeLocalStorage('supportAssistantSessionId')
      removeLocalStorage('supportAssistantCustomerId')
    }
  }, [userIsLoggedIn])

  useEffect(() => {
    if (!socket) return

    const onSessionCreated = (data: { sessionId: string; customerId: string }) => {
      if (data.customerId === customerId) {
        setLocalStorage('supportAssistantSessionId', data.sessionId)
      } else {
        setLocalStorage('supportAssistantCustomerId', data.customerId)
      }
    }

    const onSessionExpired = () => {
      const now = Date.now()
      if (now - lastExpiredAt < EXPIRY_COOLDOWN_MS) return
      lastExpiredAt = now

      disconnectGlobalSocket()
      setSocket(undefined)
      removeLocalStorage('supportAssistantSessionId')
      removeLocalStorage('supportAssistantCustomerId')
    }

    const onSessionInvalid = () => {
      disconnectGlobalSocket()
      dispatch(authLogout())
    }

    const onConnectionError = () => {
      socket.io.opts.transports = ['polling', 'websocket']
    }

    socket.on(SESSION.CREATED, onSessionCreated)
    socket.on(SESSION.EXPIRED, onSessionExpired)
    socket.on(SESSION.INVALID, onSessionInvalid)
    socket.on(CONNECTION.ERROR, onConnectionError)

    return () => {
      socket.off(SESSION.CREATED, onSessionCreated)
      socket.off(SESSION.EXPIRED, onSessionExpired)
      socket.off(SESSION.INVALID, onSessionInvalid)
      socket.off(CONNECTION.ERROR, onConnectionError)
    }
  }, [dispatch, customerId, socket])

  return {
    socket,
    resetSocket,
  }
}
